'use client';

import { useState, useEffect, useCallback } from 'react';
import { convertSanityPlanToTariff } from '../utils/dataConverter';
import { ensureUniqueTariffIds } from '../../utils/ensureUniqueTariffIds';

/**
 * Hook for loading and managing eSIM tariffs from Sanity CMS
 * @param {string} countrySlug - Country slug (e.g., 'montenegro')
 * @param {string} lang - Current language (ru/en)
 * @param {boolean} isClient - Whether we're on the client side
 * @returns {Object} Tariffs data and loading state
 */
export function useQuizTariffs(countrySlug, lang, isClient) {
    const [allTariffs, setAllTariffs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Load tariffs from Sanity CMS via API route (безопасно, без раскрытия ключей)
     */
    const loadTariffs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Используем API route вместо прямого импорта client (безопасность)
            const response = await fetch(
                `/api/sanity/tariffs?countrySlug=${encodeURIComponent(countrySlug)}&lang=${encodeURIComponent(lang)}`
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch tariffs: ${response.statusText}`);
            }

            const { countryOfferings, europeOfferings, providersData } = await response.json();

            // Convert Sanity data to tariff format
            const convertedTariffs = [];

            // Get country name from offerings if available, otherwise use slug
            let countryName = countrySlug || 'country';
            if (countryOfferings.length > 0 && countryOfferings[0]?.country?.name) {
                countryName = countryOfferings[0].country.name;
            }

            // Process country offerings
            countryOfferings.forEach(offering => {
                if (offering.plans && Array.isArray(offering.plans)) {
                    offering.plans.forEach(plan => {
                        convertedTariffs.push(
                            convertSanityPlanToTariff(plan, offering.provider, countrySlug, countryName)
                        );
                    });
                }
            });

            // Process Europe offerings
            europeOfferings.forEach(offering => {
                if (offering.plans && Array.isArray(offering.plans)) {
                    offering.plans.forEach(plan => {
                        convertedTariffs.push(
                            convertSanityPlanToTariff(plan, offering.provider, 'europe')
                        );
                    });
                }
            });

            const activeProviderCodes = new Set(
                providersData
                    .filter(provider => provider?.code)
                    .map(provider => provider.code)
            );

            const filteredTariffs = convertedTariffs.filter(tariff =>
                activeProviderCodes.has(tariff.providerId)
            );

            setAllTariffs(ensureUniqueTariffIds(filteredTariffs));
        } catch (err) {
            console.error('Error loading tariffs from Sanity:', err);
            setError(err.message || 'Failed to load tariffs');
            setAllTariffs([]);
        } finally {
            setLoading(false);
        }
    }, [countrySlug, lang]);

    /**
     * Reload tariffs
     */
    const reloadTariffs = useCallback(() => {
        loadTariffs();
    }, [loadTariffs]);

    // Load tariffs on mount and when dependencies change
    useEffect(() => {
        if (isClient) {
            loadTariffs();
        }
    }, [isClient, loadTariffs]);

    return {
        allTariffs,
        loading,
        error,
        reloadTariffs
    };
}

